import { getEventDateTime, getEventEndDateTime } from '@/lib/helpers';

// ─── Status do evento ─────────────────────────────────────────────────────
// Espelho de event_visibility.dart do Flutter.
// Variantes: 'live' | 'today' | 'tomorrow' | 'upcoming' | 'ended'

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate()
  );
}

export function getEventStatus(rawEventData) {
  const start = getEventDateTime(rawEventData);
  const end   = getEventEndDateTime(rawEventData);
  const now   = new Date();

  if (!start) return { label: 'Em breve', variant: 'upcoming' };

  // Encerrado: agora > fim + 1 min de tolerância
  if (end && now.getTime() > end.getTime() + 60_000) {
    return { label: 'Encerrado', variant: 'ended' };
  }

  // Acontecendo agora: já começou e ainda não encerrou
  if (start.getTime() <= now.getTime() && (!end || now.getTime() <= end.getTime() + 60_000)) {
    return { label: 'Acontecendo agora', variant: 'live' };
  }

  // Hoje: começa hoje mas ainda não iniciou
  if (isSameDay(start, now) && start.getTime() > now.getTime()) {
    return { label: 'Hoje', variant: 'today' };
  }

  // Amanhã
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(start, tomorrow)) {
    return { label: 'Amanhã', variant: 'tomorrow' };
  }

  return { label: 'Em breve', variant: 'upcoming' };
}

// Mesma checagem usada pelo EventStatusBadge da página de detalhe — pra
// mostrar o LiveBadge (Hero, cards de "Próximos Eventos") sem duplicar a
// lógica de "ao vivo agora" espelhada do Flutter.
export function isEventLive(rawEventData) {
  return getEventStatus(rawEventData).variant === 'live';
}

// ─── Formatação de datas ──────────────────────────────────────────────────
// Espelho de event_date_label.dart do Flutter.

// "Sábado, 12 de julho de 2025"
export function formatFullDate(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(date);
}

// "12 de julho de 2025" — usado no <title> da página de evento
export function formatDayMonthYear(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo',
  }).format(date);
}

// "Hoje" | "Amanhã" | "sáb., 12 de jul."
export function formatShortDate(date) {
  if (!date) return '';
  const now = new Date();
  if (isSameDay(date, now)) return 'Hoje';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(date, tomorrow)) return 'Amanhã';
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short', day: 'numeric', month: 'short',
  }).format(date);
}

// "20:00"
export function formatTime(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

// ─── URLs amigáveis de evento ─────────────────────────────────────────────
// Formato: /evento/{slug-do-titulo}-{id-do-firestore}. O slug é só
// estético — a consulta ao Firestore usa exclusivamente o id, que nunca
// contém "-" (é gerado no alfabeto base62 do Firestore), então extraí-lo é
// sempre pegar o trecho depois do último "-" na URL.

// "Embranza Baile 147!" → "embranza-baile-147"
export function slugify(text) {
  if (!text) return '';
  const DIACRITICS = new RegExp('[̀-ͯ]', 'g');
  return text
    .normalize('NFD')
    .replace(DIACRITICS, '') // remove acentos (marcas diacríticas)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // remove caracteres especiais
    .replace(/[\s_-]+/g, '-')     // espaços (e "-"/"_" repetidos) → "-"
    .replace(/^-+|-+$/g, '');     // apara "-" nas pontas
}

// id sozinho (sem slug) continua um link válido — mantém compatibilidade
// com URLs antigas, que usavam só o id do Firestore.
export function buildEventSlugId(title, id) {
  const slug = slugify(title);
  return slug ? `${slug}-${id}` : id;
}

export function buildEventPath(id, title) {
  return `/evento/${buildEventSlugId(title, id)}`;
}

// Extrai o id do Firestore a partir do parâmetro de rota (slug-id ou só id).
export function extractEventId(slugId) {
  if (!slugId) return '';
  const lastDash = slugId.lastIndexOf('-');
  return lastDash === -1 ? slugId : slugId.slice(lastDash + 1);
}

// ─── SEO ───────────────────────────────────────────────────────────────────

// Trunca texto pra meta description (~155 caracteres é o limite prático
// exibido pelo Google antes de cortar com "...").
export function truncate(text, maxLength = 155) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

// ─── Busca da Home ─────────────────────────────────────────────────────────
// Um único filtro reaproveitado tanto pela lista "Próximos Eventos" (Enter)
// quanto pelo dropdown de sugestões do header (enquanto digita) — pra não
// ter duas lógicas de busca divergentes. Organizador fica de fora de
// propósito: o evento só guarda organizerId, e resolver o nome exigiria uma
// leitura extra no Firestore por evento (custo alto, sem garantia de que as
// regras permitem leitura pública).
function normalizeSearchText(text) {
  const DIACRITICS = new RegExp('[̀-ͯ]', 'g');
  return (text || '')
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase();
}

export function matchesSearch(event, term) {
  const q = normalizeSearchText(term).trim();
  if (!q) return true;
  const haystack = normalizeSearchText(
    [event.title, event.description, event.location, event.city].filter(Boolean).join(' ')
  );
  return haystack.includes(q);
}
